import { useId } from 'react';

/**
 * TrophyBodySvg — Single hexagonal trophy body, parameterized by color.
 *
 * Replaces 6 individual SVG files (trophy-core-sales, trophy-axa, etc.)
 * that shared identical geometry but different gradient colors.
 *
 * Uses React useId() to generate unique SVG element IDs, preventing
 * collisions when multiple instances are rendered on the same page.
 *
 * Props:
 *  - colors: object with color keys (see BODY_COLORS in constants.js)
 *  - style, className: forwarded to root <svg>
 */
const TrophyBodySvg = ({ colors = {}, style, className, ...props }) => {
  const uid = useId();
  const _ = (name) => `${uid}${name}`;

  const {
    outerA = '#FAFAFA', outerB = '#C2C4CD',
    innerA = '#CDCFD6', innerB = '#EFEFF1',
    strokeA = '#DDDFE2', strokeB = '#FFFFFF',
    radialA = '#F5F7FF', radialB = '#B3B5C5',
    bottomB = '#D0DBFF',
    shadowR = 0.297, shadowG = 0.330, shadowB = 0.446,
  } = colors;

  /* ── Hex paths (Group A geometry — axa/neat/nlbp/mySelectra) ────────── */
  const outerHex = 'M18.8999 3.74681L7.52617 9.934C2.88752 12.4574 0 17.3153 0 22.5959L0 37.3358C0 42.6164 2.88751 47.4743 7.52617 49.9976L18.8999 56.1848C23.1947 58.5211 28.381 58.5211 32.6757 56.1848L44.0495 49.9976C48.6881 47.4743 51.5756 42.6164 51.5756 37.3358L51.5756 22.5959C51.5756 17.3153 48.6881 12.4574 44.0495 9.934L32.6757 3.74681C28.381 1.4105 23.1947 1.4105 18.8999 3.74681Z';
  const innerHex = 'M18.8949 8.27148L11.4176 12.3391C6.79642 14.853 3.91978 19.6925 3.91977 24.9533L3.91977 34.8744C3.91977 40.1351 6.79642 44.9747 11.4176 47.4886L18.8949 51.5562C23.1735 53.8837 28.3403 53.8837 32.6189 51.5562L40.0962 47.4886C44.7174 44.9747 47.5941 40.1351 47.5941 34.8744L47.5941 24.9533C47.5941 19.6926 44.7174 14.853 40.0962 12.3391L32.6189 8.27148C28.3403 5.94396 23.1735 5.94397 18.8949 8.27148Z';
  const maskHex = 'M19.3691 10.1463L12.914 13.6547C8.51571 16.0451 5.77739 20.65 5.77739 25.656L5.77738 34.2512C5.77738 39.2572 8.51571 43.8621 12.914 46.2526L19.3691 49.761C23.4367 51.9716 28.3471 51.9716 32.4146 49.761L38.8698 46.2526C43.2681 43.8621 46.0064 39.2572 46.0064 34.2512V25.656C46.0064 20.65 43.2681 16.0451 38.8698 13.6546L32.4146 10.1463C28.3471 7.93558 23.4367 7.93558 19.3691 10.1463Z';

  return (
    <svg
      viewBox="0 0 51.5759 59.9319"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      overflow="visible"
      style={{ display: 'block', width: '100%', height: '100%', ...style }}
      className={className}
      {...props}
    >
      {/* Outer hex */}
      <path d={outerHex} fill={`url(#${_('g0')})`} />

      {/* Inner hex */}
      <path d={innerHex} fill={`url(#${_('g1')})`} />

      {/* Inner hex stroke with blur */}
      <g filter={`url(#${_('f0')})`}>
        <path d={innerHex} stroke={`url(#${_('g2')})`} strokeWidth="0.847887" fill="none" />
      </g>

      {/* Masked detail group */}
      <g opacity="0.5">
        <mask id={_('m0')} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="5" y="8" width="42" height="44">
          <path d={maskHex} fill={radialA} />
        </mask>
        <g mask={`url(#${_('m0')})`}>
          {/* Radial fill */}
          <path d={maskHex} fill={`url(#${_('r3')})`} />

          {/* Light rays (constant white→transparent) */}
          <g>
            <path opacity="0.5" d="M22.5292 54.2297L21.7302 5.67751H30.0534L29.2544 54.2297H22.5292Z" fill={`url(#${_('g4')})`} />
            <path opacity="0.5" d="M22.4223 53.7982L-2.54577 12.1502L4.66237 7.98861L28.2465 50.4356L22.4223 53.7982Z" fill={`url(#${_('g5')})`} />
            <path opacity="0.5" d="M21.7287 50.4362L45.3128 7.98926L52.5209 12.1509L27.5528 53.7988L21.7287 50.4362Z" fill={`url(#${_('g6')})`} />
            <rect x="5.77748" y="27.1792" width="40.229" height="26.1257" fill={`url(#${_('g7')})`} />
          </g>

          {/* Inner shadow */}
          <g filter={`url(#${_('f1')})`}>
            <path d={maskHex} fill={radialA} fillOpacity="0.03" />
          </g>
        </g>
      </g>

      {/* ── Defs ─────────────────────────────────────────────────────────── */}
      <defs>
        {/* Foreground blur filter */}
        <filter id={_('f0')} x="3.24" y="5.85" width="45.03" height="48.13" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="bg" />
          <feBlend mode="normal" in="SourceGraphic" in2="bg" result="shape" />
          <feGaussianBlur stdDeviation="0.127183" />
        </filter>

        {/* Inner shadow filter */}
        <filter id={_('f1')} x="5.78" y="8.49" width="40.23" height="42.93" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="bg" />
          <feBlend mode="normal" in="SourceGraphic" in2="bg" result="shape" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset />
          <feGaussianBlur stdDeviation="5.70131" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowR} 0 0 0 0 ${shadowG} 0 0 0 0 ${shadowB} 0 0 0 1 0`} />
          <feBlend mode="normal" in2="shape" />
        </filter>

        {/* paint0 — outer hex fill */}
        <linearGradient id={_('g0')} x1="14.9569" y1="7.22045" x2="38.166" y2="53.3291" gradientUnits="userSpaceOnUse">
          <stop stopColor={outerA} />
          <stop offset="1" stopColor={outerB} />
        </linearGradient>

        {/* paint1 — inner hex fill */}
        <linearGradient id={_('g1')} x1="14.7507" y1="11.0372" x2="34.762" y2="47.9653" gradientUnits="userSpaceOnUse">
          <stop stopColor={innerA} />
          <stop offset="1" stopColor={innerB} />
        </linearGradient>

        {/* paint2 — hex stroke */}
        <linearGradient id={_('g2')} x1="16.9168" y1="9.6962" x2="38.4755" y2="48.4811" gradientUnits="userSpaceOnUse">
          <stop stopColor={strokeA} />
          <stop offset="1" stopColor={strokeB} />
        </linearGradient>

        {/* paint3 — radial centre fill */}
        <radialGradient id={_('r3')} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(25.8919 29.9536) rotate(90) scale(23.3524 20.1145)">
          <stop stopColor={radialA} />
          <stop offset="1" stopColor={radialB} />
        </radialGradient>

        {/* paint4/5/6 — constant white light rays */}
        <linearGradient id={_('g4')} x1="25.8918" y1="5.67751" x2="25.8918" y2="54.2297" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="1" stopColor="#F2EDED" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={_('g5')} x1="1.0583" y1="10.0694" x2="25.3344" y2="52.1169" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="1" stopColor="#F2EDED" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={_('g6')} x1="48.9169" y1="10.0701" x2="24.6407" y2="52.1175" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="1" stopColor="#F2EDED" stopOpacity="0" />
        </linearGradient>

        {/* paint7 — bottom gradient */}
        <linearGradient id={_('g7')} x1="25.892" y1="38.5081" x2="25.7706" y2="51.6862" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" stopOpacity="0" />
          <stop offset="1" stopColor={bottomB} />
        </linearGradient>
      </defs>
    </svg>
  );
};

export default TrophyBodySvg;
